import React, { useState, useEffect } from "react";
import instance from "../instance";
import { openConfirmDialog } from "./Customer/CustomerSms";
import "./Customer.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import CustomerInformation from "../components/Customer/CustomerInformation";
import ServerDetails from "../components/Customer/ServerDetails";
import SystemDetails from "../components/Customer/SystemDetails";
import DatabaseInformation from "../components/Customer/DatabaseInformation";
import AppInformation from "../components/Customer/AppInformation";
import BackupDetails from "../components/Customer/BackupDetails";
import PaymentDetails from "../components/Customer/PaymentDetails";
import SpeechSubscriptions from "../components/Customer/SpeechSubscriptions";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDate } from "./Customers/utils";

const Customer = () => {
  const token = localStorage.getItem("token");
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const code = params.get("code");

  const [customerData, setCustomerData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [speechData, setSpeechData] = useState(null);

  useEffect(() => {
    if (code) {
      fetchCustomerData();
      fetchPaymentData();
      fetchSpeechData();
    } else {
      toast.error("No code provided.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchers are recreated each render; adding them would refetch on every paint
  }, [code]);

  const fetchCustomerData = async () => {
    try {
      const response = await instance.get(`information/by-code/${code}`);
      if (response.status !== 200) {
        throw new Error("Failed to fetch customer data.");
      }
      const data = response.data;
      if (data && data.data) {
        setCustomerData(data.data);
      } else {
        toast.error("No customer data found for the given code.");
      }
    } catch (error) {
      console.error("Error fetching customer data:", error);
      toast.error("An error occurred while fetching customer data.");
    }
  };

  const fetchPaymentData = async () => {
    try {
      const response = await instance.get(`payment/by-code/${code}`);
      if (response.status === 200 && response.data?.data) {
        setPaymentData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching payment data:", error);
    }
  };

  const fetchSpeechData = async () => {
    try {
      const response = await instance.get(`speech-subscriptions/by-code/${code}`);
      if (response.status === 200 && response.data?.data) {
        setSpeechData(response.data.data);
      } else {
        setSpeechData(null);
      }
    } catch (error) {
      // إذا لم يكن هناك Speech Subscription، تجاهل الخطأ
      setSpeechData(null);
    }
  };

  const calculateSpeechStatistics = () => {
    if (!speechData || !speechData.StartDate || !speechData.IsActive || !speechData.Price) {
      return 0;
    }

    const startDate = new Date(speechData.StartDate);
    startDate.setHours(0, 0, 0, 0);

    const endDate = speechData.EndDate ? new Date(speechData.EndDate) : new Date();
    endDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (speechData.EndDate && endDate < today) {
      return 0;
    }

    const price = parseFloat(speechData.Price) || 0;
    let totalValue = 0;

    // التحقق إذا كان StartDate و EndDate في نفس اليوم من الشهر (مثلاً 22/06 و 22/07)
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    const isSameDayOfMonth = startDay === endDay;

    if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
      const month = startDate.getMonth();
      const year = startDate.getFullYear();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const daysCount = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      const dailyRate = price / daysInMonth;
      totalValue = dailyRate * daysCount;
    } else if (isSameDayOfMonth) {
      // إذا كانا في نفس اليوم من الشهر (مثلاً 22/06 و 22/07)، احسب شهر كامل = price لكل شهر
      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        // حساب شهر كامل = price
        totalValue += price;
        
        // الانتقال إلى نفس اليوم من الشهر التالي
        const month = currentDate.getMonth();
        const year = currentDate.getFullYear();
        const nextMonth = month + 1;
        const nextYear = nextMonth > 11 ? year + 1 : year;
        const nextMonthAdjusted = nextMonth > 11 ? 0 : nextMonth;
        const daysInNextMonth = new Date(nextYear, nextMonthAdjusted + 1, 0).getDate();
        const dayToSet = Math.min(startDay, daysInNextMonth);
        currentDate = new Date(nextYear, nextMonthAdjusted, dayToSet);
        
        // إذا تجاوزنا EndDate، توقف
        if (currentDate > endDate) break;
      }
    } else {
      let currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const month = currentDate.getMonth();
        const year = currentDate.getFullYear();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        
        const monthStart = currentDate > firstDayOfMonth ? currentDate : firstDayOfMonth;
        const monthEnd = endDate < lastDayOfMonth ? endDate : lastDayOfMonth;
        
        const daysCount = Math.ceil((monthEnd - monthStart) / (1000 * 60 * 60 * 24)) + 1;
        const dailyRate = price / daysInMonth;
        const monthValue = dailyRate * daysCount;
        totalValue += monthValue;
        
        currentDate = new Date(year, month + 1, 1);
      }
    }

    return totalValue;
  };

  const calculateSpeechTotals = () => {
    if (!paymentData) {
      return { monthly: 0, overall: 0 };
    }

    const price = parseFloat(paymentData.Price) || 0;
    const monthlyFees = parseFloat(paymentData.MonthlyFees) || 0;
    const systemPrice = parseFloat(paymentData.SystemPrice) || 0;
    const prepaid = parseFloat(paymentData.Prepaid) || 0;
    const remainingAmount = systemPrice - prepaid;
    const statistics = calculateSpeechStatistics();

    const monthlyTotal = price + monthlyFees + statistics;
    const overallTotal = price + remainingAmount + statistics + monthlyFees;

    return {
      monthly: monthlyTotal,
      overall: overallTotal,
    };
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const handleClick = async (name) => {
    try {
      const response = await instance.get(`Out`);
      if (response.status === 200) {
        const results = response.data.data; // الوصول إلى مصفوفة البيانات
        const dbResult = results.find(
          (result) => result.databaseName.toLowerCase() === name.toLowerCase()
        );

        if (dbResult && dbResult.data.length > 0) {
          const subscriptionUntil = formatDate(
            dbResult.data[0].SubscribeUntill
          );
          const subscriptionTypeValue = dbResult.data[0].SubscribtionType;

          // تحديد نوع الباقة بناءً على القيمة
          let subscriptionType;
          if (subscriptionTypeValue === 0) {
            subscriptionType = "Bronze";
          } else if (subscriptionTypeValue === 1) {
            subscriptionType = "Silver";
          } else if (subscriptionTypeValue === 2) {
            subscriptionType = "Golden";
          } else if (subscriptionTypeValue === 3) {
            subscriptionType = "Platinum";
          } else if (subscriptionTypeValue === 4) {
            subscriptionType = "Diamond";
          }

          const message = `Subscription for ${name}: \n Until: ${subscriptionUntil} \n Type: ${subscriptionType}`;

          confirmAlert({
            title: "Subscription Information",
            message: message,
            buttons: [
              {
                label: "OK",
                onClick: () => {},
              },
            ],
          });
        } else {
          console.error(`No data found for database ${name}`);
          toast.error(`No data found for database ${name}`);
        }
      } else {
        console.error("Failed to fetch data:", response.statusText);
        toast.error("Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      toast.error("Error fetching subscription data");
    }
  };

  const confirmDelete = () => {
    confirmAlert({
      title: "Confirm to delete",
      message: "Are you sure you want to delete the customer?",
      buttons: [
        {
          label: " Yes ",
          onClick: () => deleteCustomer(code),
        },
        {
          label: " No ",
          onClick: () => {},
        },
      ],
    });
  };

  const deleteCustomer = async (code) => {
    try {
      const response = await instance.delete(`delete/by-code/${code}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status !== 200) {
        throw new Error("Failed to delete customer.");
      }
      toast.success("Customer deleted successfully");
      navigate("/");
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast.error("Error deleting customer");
    }
  };

  if (!code) {
    return (
      <div>
        <header>
          <h1>No customer code provided</h1>
        </header>
        <main>
          <Link to="/">Return to Main</Link>
        </main>
      </div>
    );
  }

  return (
    <div>
      <header>
        <h1>
          {customerData
            ? capitalizeFirstLetter(customerData.Name)
            : "Customer Details"}
        </h1>
      </header>

      <main>
        <div className="sidebar">
          <Link to="/" className="sidebar-button">
            ←Main
          </Link>
          <button
            onClick={() =>
              document
                .getElementById("info-section")
                .scrollIntoView({ behavior: "smooth" })
            }
            className="sidebar-button"
          >
            Information
          </button>
          <button
            onClick={() =>
              document
                .getElementById("server-section")
                .scrollIntoView({ behavior: "smooth" })
            }
            className="sidebar-button"
          >
            Server
          </button>
          <button
            onClick={() =>
              document
                .getElementById("system-section")
                .scrollIntoView({ behavior: "smooth" })
            }
            className="sidebar-button"
          >
            System
          </button>
          <button
            onClick={() =>
              document
                .getElementById("database-section")
                .scrollIntoView({ behavior: "smooth" })
            }
            className="sidebar-button"
          >
            Database
          </button>
          <button
            onClick={() =>
              document
                .getElementById("app-section")
                .scrollIntoView({ behavior: "smooth" })
            }
            className="sidebar-button"
          >
            App
          </button>
          <button
            onClick={() =>
              document
                .getElementById("backup-section")
                .scrollIntoView({ behavior: "smooth" })
            }
            className="sidebar-button"
          >
            Backup
          </button>

          <button
            onClick={() =>
              document
                .getElementById("payment-section")
                .scrollIntoView({ behavior: "smooth" })
            }
            className="sidebar-button"
          >
            Payment
          </button>
          <button
            onClick={() =>
              document
                .getElementById("speech-section")
                .scrollIntoView({ behavior: "smooth" })
            }
            className="sidebar-button"
          >
            Speech
          </button>
          <button
            onClick={() => navigate(`/price-all?name=${customerData.Name}`)}
            className="sidebar-button sidebar-button--secondary"
          >
            Price
          </button>
          <button
            onClick={() => navigate(`/phone?name=${customerData.Name}`)}
            className="sidebar-button sidebar-button--secondary"
          >
            Partner
          </button>

          <button
            onClick={() => {
              handleClick(customerData.Name);
            }}
            className="sidebar-button sidebar-button--secondary"
          >
            Subscribe info
          </button>
          <button
            onClick={() => {
              openConfirmDialog(customerData.Code);
            }}
            className="sidebar-button sidebar-button--secondary"
          >
            SMS
          </button>
          <button
            onClick={() => navigate(`/customer-orders?code=${customerData.Code}&name=${customerData.Name}`)}
            className="sidebar-button sidebar-button--secondary"
          >
            Orders
          </button>
        </div>
        <section id="info-section">
          <CustomerInformation />
        </section>
        <section id="server-section">
          <ServerDetails />
        </section>
        <section id="system-section">
          <SystemDetails />
        </section>
        <section id="database-section">
          <DatabaseInformation />
        </section>
        <section id="app-section">
          <AppInformation />
        </section>
        <section id="backup-section">
          <BackupDetails />
        </section>
        <section id="payment-section">
          <PaymentDetails />
        </section>
        <section id="speech-section">
          <SpeechSubscriptions />
        </section>
          {paymentData && (
            <section id="speech-totals-section" style={{ marginTop: "20px", marginBottom: "20px" }}>
              <h2>Total</h2>
              <ul>
                <li>
                  Monthly Total:{" "}
                  <span
                    className="info"
                    style={{
                      background: "rgba(0, 150, 255, 0.3)",
                      fontWeight: "bold",
                      fontSize: "18px",
                    }}
                  >
                    {calculateSpeechTotals().monthly.toFixed(2)}
                  </span>
                  <span style={{ marginLeft: "10px", fontSize: "14px", color: "#666" }}>
                    ({[
                      `Price: ${parseFloat(paymentData.Price) || 0}`,
                      parseFloat(paymentData.MonthlyFees) !== 0 ? `Monthly Fees: ${parseFloat(paymentData.MonthlyFees) || 0}` : null,
                      calculateSpeechStatistics() !== 0 ? `Statistics: ${calculateSpeechStatistics().toFixed(2)}` : null,
                    ].filter(Boolean).join(" + ")})
                  </span>
                </li>
                <li>
                  Overall Total:{" "}
                  <span
                    className="info"
                    style={{
                      background: "rgba(0, 200, 100, 0.3)",
                      fontWeight: "bold",
                      fontSize: "18px",
                    }}
                  >
                    {calculateSpeechTotals().overall.toFixed(2)}
                  </span>
                  <span style={{ marginLeft: "10px", fontSize: "14px", color: "#666" }}>
                    ({[
                      `Price: ${parseFloat(paymentData.Price) || 0}`,
                      parseFloat(paymentData.MonthlyFees) !== 0 ? `Monthly Fees: ${parseFloat(paymentData.MonthlyFees) || 0}` : null,
                      ((parseFloat(paymentData.SystemPrice) || 0) - (parseFloat(paymentData.Prepaid) || 0)) !== 0 ? `Remaining Amount: ${((parseFloat(paymentData.SystemPrice) || 0) - (parseFloat(paymentData.Prepaid) || 0)).toFixed(2)}` : null,
                      calculateSpeechStatistics() !== 0 ? `Statistics: ${calculateSpeechStatistics().toFixed(2)}` : null,
                    ].filter(Boolean).join(" + ")})
                  </span>
                </li>
              </ul>
            </section>
          )}
        <button
          onClick={confirmDelete}
          className="button2"
          style={{
            color: "white",
            background: "#cb0202",
            fontWeight: "bold",
            marginBottom: "80px",
          }}
        >
          Delete Customer
        </button>
      </main>
    </div>
  );
};

export default Customer;
